import {
  ApplicationContentSection,
  ApplicationContentSectionView,
  ApplicationContentView,
} from 'store/types/ApplicationContent';
import QuestionType from 'store/enums/QuestionType';
import ApplicationReviewStatus from 'store/enums/ApplicationReviewStatus';
import ApplicationQuestion, { ApplicationQuestionView, SublistQuestion } from 'store/types/ApplicationQuestion';
import ApplicationAnswer, { SublistAnswer } from 'store/types/ApplicationAnswer';
import ApplicationType from 'store/types/ApplicationType';
import React from 'react';
import StatusLabel from 'components/shared/StatusLabel';
import ApplicationStatus from 'store/enums/ApplicationStatus';
import CustomerType from 'store/enums/CustomerType';
import applicationStatusConfig from 'store/configs/ApplicationStatusConfig';
import { getISODateString } from 'util/Format';
import moment from 'moment/moment';
import { Alert, AlertTitle } from '@mui/material';
import Application from 'store/types/Application';
import { ApplicationProgressCalculation } from 'store/types/SiteConfig';
import ProgressBar from 'components/shared/ProgressBar';

export const getQuestionFieldName = (questionId: string): string => `question-${questionId}`;

export const getQuestionId = (questionFieldName: string): string => {
  if (Number.isInteger(parseInt(questionFieldName, 10))) {
    return questionFieldName;
  }

  const matches: RegExpMatchArray | null = questionFieldName.match(/question-(\d+)$/);
  return matches ? matches[1] : '';
};

export const getSublistQuestionId = (questionFieldName: string): string => {
  if (Number.isInteger(parseInt(questionFieldName, 10))) {
    return questionFieldName;
  }

  const matches: RegExpMatchArray | null = questionFieldName.match(/sublistQuestion-(\d+)$/);
  return matches ? matches[1] : '';
};

export const getExplanationFieldName = (parentFieldName: string): string => `${parentFieldName}-explanation`;

export const getCheckboxFieldName = (parentFieldName: string): string => `${parentFieldName}-checkbox`;

export const getSectionId = (index: number): string => `app-section-${index}`;

export const getReviewSectionId = (index: number): string => `app-review-section-${index}`;

export const getFormattedSections = (initialList: ApplicationContentSection[] = []): ApplicationContentSectionView[] =>
  getSorterSequenceList(initialList).map((section) => ({
    ...section,
    questions: getSorterSequenceList(section.questions || []).map((question) => ({
      ...question,
      sublistQuestions: question.sublistQuestions && getSorterSequenceList(question.sublistQuestions),
      fieldName: getQuestionFieldName(question.id),
      additionalInfo: question.additionalInfo || '',
    })),
  }));

const getSublistAnswerValues = (questions: SublistQuestion[]): Array<SublistAnswer[]> => {
  const result: Array<SublistAnswer[]> = [];

  questions?.forEach(({ answers, id: sublistQuestionId, content, type }) =>
    answers.forEach(({ referenceKey, answer, answerId, files }) => {
      const foundIndex = result.findIndex(
        (answerArray) => !!answerArray.find((answer) => answer.referenceKey === referenceKey)
      );
      const newValue = {
        referenceKey,
        answer: answer,
        answerId,
        type,
        sublistQuestionId,
        content,
        files,
      };

      if (foundIndex !== -1) {
        result[foundIndex].push(newValue);
      } else {
        result.push([newValue]);
      }
    })
  );

  return result;
};

const getSorterSequenceList = <T extends { sequence?: number }>(list: T[]) =>
  list.sort((a, b) =>
    !a.sequence && a.sequence !== 0 ? 1 : !b.sequence && b.sequence !== 0 ? -1 : a.sequence - b.sequence
  );

export const isReviewEditable = (status?: ApplicationReviewStatus): boolean =>
  !status ||
  status === ApplicationReviewStatus.PendingInfo ||
  status === ApplicationReviewStatus.PendingReview ||
  status === ApplicationReviewStatus.WaitingForReview;

export const getQuestionsDefaultValues = ({ sections = [] }: ApplicationContentView) => {
  let result = {};

  sections.forEach(({ questions = [] }: ApplicationContentSectionView) => {
    questions.forEach(({ type, fieldName, options = [], answer, sublistQuestions }: ApplicationQuestionView) => {
      switch (type) {
        case QuestionType.Checkbox:
        case QuestionType.MultiSelectDropDown:
          result = {
            ...result,
            [fieldName]: options.map(({ value = '', name, isSelected, includeExplanation, explanation }) => ({
              id: value,
              name,
              value: isSelected,
              includeExplanation,
              explanation,
            })),
          };
          break;
        case QuestionType.File:
          result = { ...result, [fieldName]: [] };
          break;
        case QuestionType.SubList:
        case QuestionType.Address:
          result = {
            ...result,
            [fieldName]: getSublistAnswerValues(sublistQuestions || []),
          };
          break;
        case QuestionType.Date:
          result = { ...result, [fieldName]: answer ? moment(answer) : '' };
          break;
        case QuestionType.Text:
        case QuestionType.MultilineText:
        case QuestionType.DropdownCustomList:
        case QuestionType.Radio:
        case QuestionType.Dropdown:
        case QuestionType.Phone:
        case QuestionType.Email:
          result = { ...result, [fieldName]: answer || '' };
          break;
      }
    });
  });
  return result;
};

const getApplicationQuestionType = (
  questionId: ApplicationQuestion['id'],
  questions: ApplicationQuestion[] = []
): QuestionType => {
  const foundQuestion: ApplicationQuestion | undefined = questions.find(({ id }) => id === questionId);

  return foundQuestion ? foundQuestion.type : QuestionType.Text;
};

export const getAnswersData = (formValues: any, questionsList: ApplicationQuestion[] = []): ApplicationAnswer[] => {
  const result: ApplicationAnswer[] = [];
  Object.keys(formValues).forEach((key: string) => {
    const questionId: string = getQuestionId(key);
    if (questionId) {
      const formValue = formValues[key];
      const explanationFormValue: string | undefined = formValues[getExplanationFieldName(key)];
      const questionType: QuestionType = getApplicationQuestionType(questionId, questionsList);

      switch (questionType) {
        case QuestionType.Text:
        case QuestionType.MultilineText:
        case QuestionType.Phone:
        case QuestionType.Email:
          result.push({ questionId, answer: formValue });
          break;
        case QuestionType.Checkbox:
        case QuestionType.MultiSelectDropDown:
          result.push({
            questionId,
            selections: (formValue || [])
              .filter(({ value }: any) => !!value)
              .map(({ id, explanation }: any) => ({ value: id, explanation })),
          });
          break;
        case QuestionType.File:
          result.push({ questionId, files: formValue });
          break;
        case QuestionType.Date:
          result.push({ questionId, answer: getISODateString(formValue) || '' });
          break;
        case QuestionType.SubList:
        case QuestionType.Address:
          result.push({ questionId, sublistAnswers: formValue });
          break;
        case QuestionType.DropdownCustomList:
          result.push({ questionId, answer: formValue });
          break;
        case QuestionType.Dropdown:
        case QuestionType.Radio:
          if (formValue) {
            result.push({ questionId, selections: [{ value: formValue, explanation: explanationFormValue }] });
          }
          break;
      }
    }
  });
  return result;
};

export const calculateApplicationPercentByStatus = (
  applicationStatus: string,
  isParent: boolean,
  applicationProgressCalculations: ApplicationProgressCalculation[]
) => {
  const match = applicationProgressCalculations.find(
    (item: ApplicationProgressCalculation) => item.status.toLowerCase() === applicationStatus.toLowerCase()
  );

  return match?.percentage ?? null;
};

export const calculateSumOfApplicationPercent = (
  application: Application,
  applicationProgressCalculations: ApplicationProgressCalculation[]
) => {
  if (!application?.children?.length) {
    return null;
  }

  // Count all children (not denied) plus single parent
  const applicationCount =
    application.children.filter((item) => item.status.toLowerCase() !== ApplicationStatus.Denied.toLowerCase()).length +
    1;

  const parentPercentage = calculateApplicationPercentByStatus(
    application.status,
    true,
    applicationProgressCalculations
  );

  if (parentPercentage === null) {
    return null;
  }

  const sumChildPercentages: number = application.children.reduce((acc: number, currentValue: Application) => {
    return (
      acc + (calculateApplicationPercentByStatus(currentValue.status, false, applicationProgressCalculations) ?? 0)
    );
  }, 0);

  const total = (parentPercentage + sumChildPercentages) / applicationCount;

  return Math.round(total);
};

export const getApplicationStatusLabel = (
  status: string,
  record?: Application,
  applicationProgressCalculations?: ApplicationProgressCalculation[]
): React.ReactNode => {
  const statusConfig = applicationStatusConfig[status as ApplicationStatus];

  if (record && applicationProgressCalculations) {
    const percentSum: number | null = calculateSumOfApplicationPercent(record, applicationProgressCalculations);

    if (percentSum !== null) {
      return getApplicationStatusProgress(percentSum);
    }
  }

  return (
    <div style={{ textAlign: 'right' }}>
      <StatusLabel
        theme={statusConfig?.theme || 'grey'}
        status={statusConfig?.name || status}
        fullWidth={!record?.parentId}
      />
    </div>
  );
};

export const getApplicationStatusProgress = (percentage: number): React.ReactNode => {
  let color = '';

  switch (true) {
    case percentage >= 0 && percentage <= 25:
      color = '#e60000'; // red
      break;
    case percentage >= 26 && percentage <= 50:
      color = '#ebeb00'; // yellow
      break;
    case percentage >= 51 && percentage <= 99:
      color = '#ffa500'; // orange
      break;
    default:
      color = '#008000'; // green
      break;
  }

  return (
    <div style={{ minWidth: '150px' }}>
      <ProgressBar value={percentage} label={`<b>${percentage}%</b>`} color={color} />
    </div>
  );
};

export const getApplicationStatusAlert = (application: ApplicationContentView) => {
  const statusConfig = applicationStatusConfig[application.status as ApplicationStatus];
  switch (application.status) {
    case ApplicationStatus.Approved:
    case ApplicationStatus.Denied:
    case ApplicationStatus.InformationRequired:
      return (
        <Alert severity={statusConfig?.severity || 'info'}>
          <AlertTitle>{statusConfig.name}</AlertTitle>
          {application?.decisionComment}
        </Alert>
      );
    default:
      return <></>;
  }
};

export const getFormattedApplicationStatus = (status: string): string | ApplicationStatus => {
  const formattedStatus: string = status.replace(/\s/g, '').toLowerCase();

  return Object.values(ApplicationStatus).includes(formattedStatus as ApplicationStatus) ? formattedStatus : status;
};

export const getFormattedApplicationReviewStatus = (status: string): string | ApplicationReviewStatus => {
  const formattedStatus: string = status.replace(/\s/g, '').toLowerCase();

  return Object.values(ApplicationReviewStatus).includes(formattedStatus as ApplicationReviewStatus)
    ? formattedStatus
    : status;
};

export const getCategoryList = (appList: ApplicationType[] = []): string[] =>
  appList.length ? Array.from(new Set(appList.map((item) => item.category))).sort() : [];

export const getFilteredAppTypes = (
  list: ApplicationType[],
  userRoles: string[],
  selectedCustomerType: CustomerType = CustomerType.Individual
): ApplicationType[] => {
  return list
    .filter(
      ({ customerType }) =>
        (!customerType && selectedCustomerType === CustomerType.Individual) || customerType === selectedCustomerType
    )
    .filter(({ roles, isPublic }) => isPublic || !!roles.filter(({ name }) => userRoles.includes(name)).length);
};

export const getApplicationTitle = (app: ApplicationContentView) => {
  return `${app.applicantName}: ${app.title || app.typeName} ${app.campaignName} (Ref# ${app.id})`;
};
